export const COOKIE_STORAGE_KEY = "smartessay.cookie.v1";

export const COOKIE_BANNER_TEXT =
  "We use essential cookies to make the site work. No tracking ads. You can opt out of analytics anytime.";

export const HOMEPAGE_INTEGRITY_BANNER_TEXT =
  "SmartEssay.ai is a writing assistant. Final work must be your own. Submitting unedited AI content may violate academic integrity policies.";

export const FOOTER_TAGLINE =
  "© 2026 SmartEssay.ai, a Tilt University project • Made to help students learn • All users responsible for academic honesty";

export const FOOTER_DATA_NOTE = "We never train on your data without permission.";

export const PRIVACY_LAST_UPDATED = "May 30, 2026";

// Age gate (COPPA)
export const AGE_GATE_DOB_LABEL = "Date of Birth (you must be 13 or older)";
export const AGE_GATE_CHECKBOX =
  "✅ I confirm I am at least 13 years old. Users under 13 are not permitted on SmartEssay.ai.";
export const AGE_GATE_ERROR =
  "You must be 13 or older to create an account. Please ask a parent/guardian or use a school-approved tool instead.";
export const AGE_GATE_SUCCESS = "Great! Welcome to SmartEssay.ai 🎉";
export const AGE_GATE_HELPER =
  "This helps us keep SmartEssay safe for students. We only store your birth year.";
export const AGE_GATE_BANNER =
  "Reminder: SmartEssay.ai is for users 13 and older. If you're 13–17, please use it with parent/guardian permission and in line with your school's policies.";
export const AGE_BANNER_STORAGE_KEY = "smartessay.age-banner.v1";

export type PrivacySection = { id?: string; title: string; body: string[] };

export const PRIVACY_INTRO_BULLETS = [
  "We never train on your essays or prompts unless you explicitly opt in.",
  "All content is encrypted in transit and at rest.",
  "We do not sell your data.",
  "For schools: we are happy to sign DPAs and are FERPA / COPPA aware.",
];

export const PRIVACY_SECTIONS: PrivacySection[] = [
  {
    title: "1. Information We Collect",
    body: [
      "Account information you provide directly: name, email address, password (hashed), and — if you subscribe — billing details handled by our payment processor (Stripe). We do not store your full card number.",
      "Content you submit: essay titles, prompts, source text you paste or upload, generated outlines, drafts, exported PDFs (MLA / APA 7), peer-review feedback, and history records associated with your account.",
      "Voice-matching samples: writing samples you paste, plus optional voice or video recordings you upload (or capture via the in-app dictation tool) so the AI can learn your sentence length, vocabulary, contractions, and rhythm. Audio and video files are transcribed to text; the original media is deleted after transcription unless you save it to a permanent voice profile.",
      "Saved voice profiles: the extracted style signals (e.g., average sentence length, vocabulary tier, favorite connectors, tone) attached to your account when you opt to save a profile from the My Voice settings.",
      "Technical information collected automatically: device type, browser, IP address (truncated where possible), and basic usage events used to keep the service reliable.",
    ],
  },
  {
    title: "2. How We Use Your Information",
    body: [
      "To provide the service: generate outlines and full sample drafts, run peer-review feedback, export formatted MLA and APA 7 PDFs (including auto-italicized titles and journal names), save your history, authenticate your account, and process payments.",
      "To power voice matching: analyze your writing samples and any voice or video recordings you provide so generated drafts mirror your personal style, and to apply our anti-repetition guard that avoids common AI-tell words and patterns.",
      "To improve reliability and fix bugs using aggregated, non-identifying logs.",
      "To respond to support requests and send essential account or service emails.",
      "We do not use your essays, prompts, voice/video samples, transcripts, or generated content to train AI models.",
    ],
  },
  {
    id: "training",
    title: "3. AI Training & Your Content",
    body: [
      "We never train AI models on your essays, prompts, source text, voice or video recordings, transcripts, saved voice profiles, or generated outputs unless you explicitly opt in.",
      "Third-party AI providers we use to generate outputs and transcribe audio/video operate under zero-retention or short-retention agreements appropriate for educational use; they do not use submitted content to train their public models.",
    ],
  },
  {
    id: "voice",
    title: "4. Voice & Video Samples (Voice Matching)",
    body: [
      "Voice matching is optional. You control it from the My Voice settings card and the per-essay voice panel, and you can turn it Off, use a Saved Voice profile, or use an Ad-hoc Sample for a single draft.",
      "When you upload a voice or video recording, we transcribe it to text using a third-party speech-to-text provider under a zero-retention agreement. We extract style signals (sentence length, vocabulary tier, contractions, favorite connectors, tone) from the transcript.",
      "Original audio and video files are not retained after transcription unless you explicitly attach them to a saved voice profile. Transcripts and extracted style signals are stored only when you save a voice profile or use an ad-hoc sample for that generation.",
      "We do not use voice or video samples for speaker identification, biometric authentication, advertising, or any purpose other than style matching for your own drafts.",
      "You can delete any saved voice profile, sample, or recording at any time from the My Voice settings; deletion is immediate and purged from backups within 30 days.",
    ],
  },

  {
    title: "5. Sharing",
    body: [
      "We do not sell your personal data to anyone, ever.",
      "We share data only with the service providers required to operate SmartEssay (cloud hosting, our AI provider, our payment processor) under contracts that limit how they may use your data.",
      "We may disclose information if required by law, to protect users from harm, or to enforce our Terms.",
    ],
  },
  {
    title: "6. Your Rights",
    body: [
      "You can access, update, export, or delete your account and history at any time from inside the app or by contacting us.",
      "Depending on where you live, you may have additional rights under GDPR, CCPA, or similar laws — including the right to request a copy of your data or to object to processing.",
      "Email support@tilt.university for any privacy request and we will respond within 30 days.",
    ],
  },
  {
    title: "7. Children Under 13",
    body: [
      "SmartEssay is not directed to children under 13. We do not knowingly collect personal information from children under 13.",
      "If you are a parent or guardian and believe your child has provided us information, contact us and we will delete it.",
      "Students aged 13–17 should use SmartEssay only with parental or guardian consent and within their school's policies.",
    ],
  },
  {
    title: "8. Data Retention",
    body: [
      "We retain your account and history for as long as your account is active so you can keep using SmartEssay.",
      "When you delete an outline it is removed from your history immediately and purged from our backups within 30 days.",
      "When you delete your account we delete your personal data within 30 days, except where we are required to keep limited billing records for tax and accounting purposes.",
    ],
  },
  {
    title: "9. Security",
    body: [
      "All content is encrypted in transit (TLS) and at rest.",
      "Passwords are stored using industry-standard hashing.",
      "We follow the principle of least privilege internally — only the team members who need access for support or maintenance have it.",
    ],
  },
  {
    title: "10. Cookies",
    body: [
      "We use essential cookies to keep you signed in and to remember your preferences. We do not run third-party ad tracking.",
      "Optional analytics cookies help us understand which features students rely on so we can improve them. You can opt out from the cookie banner at any time.",
    ],
  },
  {
    title: "11. For Schools (FERPA / COPPA)",
    body: [
      "SmartEssay is designed to be safe for school use. We are happy to sign Data Processing Agreements (DPAs) and standard FERPA / COPPA addenda.",
      "For school licensing or to request a DPA, contact support@tilt.university.",
    ],
  },
  {
    title: "12. International Users",
    body: [
      "SmartEssay is operated from the United States. If you use the service from outside the U.S., your information will be transferred to and processed in the U.S. under appropriate safeguards.",
    ],
  },
  {
    title: "13. Changes to This Policy",
    body: [
      "We will update this page when our practices change. Material changes will be communicated through the service or by email. Continued use after a change means you accept the updated policy.",
    ],
  },
  {
    title: "14. Contact",
    body: [
      "Questions about your privacy? Email support@tilt.university or use the contact page.",
    ],
  },
];
